import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { fadeUp, fadeIn, viewport } from "@/lib/motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOURNEYS = {
  "from-self-to-system": {
    n: "01",
    title: "From Self to System",
    subtitle: "Inner worlds & the systems we create",
    quote:
      "A framework exploring the relationship between inner worlds and the systems we create.",
    body:
      "From personal awareness to collective impact, this framework explores how our inner states, beliefs and ways of relating quietly shape the cultures, communities and systems around us, and how we build and design accordingly.",
  },
  "your-inner-temple": {
    n: "03",
    title: "Your Inner Temple",
    subtitle: "A reflective journey to the landscapes within",
    quote: "A reflective journey to the landscapes within. The inner gateways.",
    body:
      "A living framework designed as a journey inward, exploring themes such as belonging, self-love, purpose, forgiveness, service and community. Built around a series of gateways, it invites deeper inquiry into the experiences, patterns and questions shaping who we become.",
  },
  "your-journey": {
    n: "04",
    title: "Your Journey",
    subtitle: "A 4-door chapter",
    quote:
      "Aligning the inner world with the outer, so it can ripple into the collective.",
    body:
      "A 4-door chapter supporting leaders, creatives and people with a drive for impact and creation to align their inner world with their outer world, so it can ripple out to the collective and the systems they are part of.",
  },
  "architecture-of-community": {
    n: "02",
    title: "The Architecture of Community",
    subtitle: "Belonging, by design",
    quote:
      "Exploring the visible and invisible layers that create belonging and form a community.",
    body:
      "This framework investigates what allows communities and ecosystems to thrive beyond shared interests or proximity. It explores both visible structures, participation, spaces, governance and roles, and invisible foundations such as trust, care, identity, accountability and culture.",
  },
};

export default function JourneyRequestPage() {
  const { slug } = useParams();
  const item = JOURNEYS[slug];

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!item) {
    return (
      <div className="bg-bone min-h-screen">
        <Nav />
        <div className="py-40 text-center">
          <p className="font-serif italic text-3xl text-charcoal">Framework not found.</p>
          <Link
            to="/#frameworks"
            className="mt-6 inline-block text-bronze link-underline text-sm uppercase tracking-[0.22em]"
          >
            Back to frameworks
          </Link>
        </div>
      </div>
    );
  }

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        intent: "journey",
        message: `[Framework request: ${item.title}]\n\n${form.message || "(no message)"}`,
        preferred_time: "",
      });
      setDone(true);
      toast.success("Received. I'll respond personally, with care.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something didn't go through. Please try again or email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-forest min-h-screen text-bone">
      <Nav />

      {/* HERO */}
      <header
        className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
        data-testid="journey-page"
      >
        {/* faint orbital */}
        <svg
          className="absolute -top-20 -right-32 w-[640px] opacity-10 pointer-events-none"
          viewBox="0 0 400 400"
          fill="none"
        >
          <circle cx="200" cy="200" r="190" stroke="#F4EFE6" strokeWidth="0.5" strokeDasharray="2 6" />
          <circle cx="200" cy="200" r="130" stroke="#F4EFE6" strokeWidth="0.5" />
          <circle cx="200" cy="200" r="70" stroke="#F4EFE6" strokeWidth="0.5" strokeDasharray="1 3" />
          <circle cx="20" cy="200" r="3" fill="#8C6A48" />
        </svg>

        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Link
            to="/#frameworks"
            data-testid="journey-back"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-clay/80 hover:text-bronze transition-colors duration-500"
          >
            <ArrowLeft size={14} /> Back to frameworks
          </Link>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-14 text-[10px] uppercase tracking-[0.3em] text-bronze"
          >
            ✦ &nbsp; Framework {item.n} &nbsp; ✦
          </motion.p>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            data-testid="journey-title"
            className="mt-6 font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight italic text-bone"
          >
            {item.title}
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-4 text-[11px] uppercase tracking-[0.22em] text-clay/80"
          >
            {item.subtitle}
          </motion.p>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-10 font-serif italic text-2xl md:text-3xl text-bronze leading-snug max-w-2xl mx-auto"
          >
            &ldquo;{item.quote}&rdquo;
          </motion.p>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-10 text-base md:text-lg text-bone/80 leading-relaxed font-light max-w-2xl mx-auto"
          >
            {item.body}
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-12 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-forestDeep border border-clay/30 text-clay/90 text-[10px] uppercase tracking-[0.3em]"
          >
            <span className="w-2 h-2 rounded-full bg-bronze animate-pulse" />
            Full framework page, in the making
          </motion.div>
        </div>
      </header>

      {/* REQUEST FORM */}
      <section className="relative bg-bone text-charcoal py-24 md:py-32" data-testid="journey-request-form-section">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-6">
            ✦ &nbsp; Request &nbsp; ✦
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
            Want to{" "}
            <span className="italic text-bronze">learn more</span>?
          </h2>
          <p className="mt-6 text-ink leading-relaxed font-light">
            The full framework page is being written, slowly. Leave your name
            and I&apos;ll send a personal note when the next gathering or
            material for{" "}
            <span className="italic font-serif text-bronze text-[1.2em]">
              {item.title}
            </span>{" "}
            opens.
          </p>

          <form onSubmit={onSubmit} className="mt-12 space-y-7 text-left">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                  Your name
                </label>
                <input
                  data-testid="journey-name"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Who is writing"
                  className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                  Email
                </label>
                <input
                  data-testid="journey-email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@somewhere.com"
                  className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-stone block mb-2">
                Anything you&apos;d like to share <span className="lowercase text-stone/60">(optional)</span>
              </label>
              <textarea
                data-testid="journey-message"
                value={form.message}
                onChange={onChange("message")}
                rows={4}
                placeholder="What draws you to this framework?"
                className="w-full bg-transparent border-b border-stone/50 py-3 text-charcoal placeholder:text-stone/60 focus:outline-none focus:border-forest transition-colors resize-none"
              />
            </div>
            <div className="pt-12">
              <button
                type="submit"
                data-testid="journey-submit"
                disabled={submitting || done}
                className="btn-pill group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-charcoal text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-forest disabled:opacity-60"
              >
                {done ? "Received, thank you" : submitting ? "Sending…" : `Request ${item.title}`}
                {!done && (
                  <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
                )}
              </button>
            </div>
          </form>

          {/* Let's explore more */}
          <div className="mt-20 pt-12 border-t border-stone/30 text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-4">
              ✦ &nbsp; Or simply &nbsp; ✦
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-charcoal leading-snug">
              Let&apos;s <span className="italic text-bronze">explore more</span> together.
            </h3>
            <Link
              to="/#connect"
              data-testid="journey-explore-more"
              className="btn-pill mt-8 inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-charcoal/40 text-charcoal text-[11px] uppercase tracking-[0.22em] hover:bg-charcoal hover:text-bone transition-colors duration-500"
            >
              Start a conversation
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
